import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ShoppingBasket, ChevronLeft, ChevronRight, BadgePercent, ArrowRight } from "lucide-react";
import { STORE } from "../lib/api";
import { money } from "../lib/shop";

const dealText = (d) => {
    const core =
        d.type === "percentage" ? `${d.value}% off`
        : d.type === "bxgy" ? `Buy ${d.buy_qty} get ${d.free_qty} free`
        : `${money(d.value)} off`;
    const scope = d.min_spend
        ? ` when you spend ${money(d.min_spend)}`
        : d.applies_to && d.applies_to !== "all"
          ? " on selected items"
          : " storewide";
    return core + scope;
};

const SLIDE_MS = 5500;

export const HeroCarousel = ({ banners = [], deals = [] }) => {
    const slides = useMemo(() => {
        const s = [
            {
                kind: "hero",
                id: "storefront",
                image: "/assets/storefront.png",
                alt: "Freddie's Afro Caribbean Store storefront on Whitley Street, Reading",
                title: "Welcome to Freddie's Afro Caribbean Store",
                text: "Your local destination for African and Caribbean groceries, meat, poultry and fresh produce.",
            },
        ];
        banners
            .filter((b) => b.image)
            .forEach((b) =>
                s.push({ kind: "banner", id: b.id, image: b.image, alt: b.title, title: b.title, text: b.text, cta_label: b.cta_label, cta_url: b.cta_url })
            );
        deals.forEach((d) => s.push({ kind: "deal", id: `deal-${d.code}`, deal: d }));
        return s;
    }, [banners, deals]);

    const count = slides.length;
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const go = (i) => setIndex(((i % count) + count) % count);

    useEffect(() => {
        if (index >= count) setIndex(0);
    }, [count, index]);

    useEffect(() => {
        if (paused || count < 2) return undefined;
        const t = setInterval(() => setIndex((i) => (i + 1) % count), SLIDE_MS);
        return () => clearInterval(t);
    }, [paused, count]);

    return (
        <section
            className="relative"
            data-testid="hero-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 md:pt-8">
                <div className="relative overflow-hidden rounded-[1.75rem] md:rounded-[2.5rem] shadow-2xl ring-1 ring-black/5 h-[430px] sm:h-[500px] md:h-[560px]">
                    {slides.map((s, i) => {
                        const active = i === index;
                        return (
                            <div
                                key={s.id}
                                data-testid={`hero-slide-${i}`}
                                aria-hidden={!active}
                                className={`absolute inset-0 transition-opacity duration-1000 ease-out ${active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
                            >
                                {s.kind === "deal" ? (
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#0033A0] via-[#002271] to-[#000f30]">
                                        <BadgePercent className="absolute -right-10 -bottom-14 h-72 w-72 md:h-96 md:w-96 text-white/[0.07] rotate-12" />
                                        <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-[#FFC72C]/10 blur-2xl" />
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={s.image}
                                            alt={s.alt}
                                            className={`absolute inset-0 h-full w-full object-cover object-[center_30%] transition-transform duration-[6000ms] ease-out ${active ? "scale-105" : "scale-100"}`}
                                            {...(i === 0 ? { "data-testid": "hero-image" } : {})}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#000f30]/85 via-[#0033A0]/45 to-transparent" />
                                        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#000f30]/70 to-transparent" />
                                    </>
                                )}
                                <div className="relative h-full flex items-end md:items-center px-6 sm:px-16 lg:px-20 pb-16 md:pb-0">
                                    <div className={`max-w-2xl transition-all duration-700 delay-150 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                                        {s.kind === "deal" ? (
                                            <>
                                                <span className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] px-4 py-1.5 font-heading font-bold text-xs tracking-widest uppercase text-[#0033A0]">
                                                    <BadgePercent className="h-4 w-4" /> Limited time offer
                                                </span>
                                                <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight capitalize" data-testid={`hero-deal-text-${s.deal.code}`}>
                                                    {dealText(s.deal)}
                                                </h1>
                                                <p className="mt-3 md:mt-4 text-base md:text-lg text-blue-100 leading-relaxed">
                                                    Use code{" "}
                                                    <span className="font-mono font-bold tracking-wider bg-white/10 border border-dashed border-[#40E0D0] rounded-lg px-2 py-0.5 text-[#40E0D0]">
                                                        {s.deal.code}
                                                    </span>{" "}
                                                    at checkout
                                                    {s.deal.ends_at && (
                                                        <>
                                                            {" "}· ends {new Date(s.deal.ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                                        </>
                                                    )}
                                                </p>
                                                <div className="mt-6 md:mt-8">
                                                    <Link
                                                        to="/products"
                                                        data-testid={`hero-deal-cta-${s.deal.code}`}
                                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#40E0D0] px-8 py-3.5 font-heading font-bold text-[#0033A0] transition-all hover:bg-[#2fd4c4] hover:gap-3"
                                                    >
                                                        <ShoppingBasket className="h-5 w-5" />
                                                        Shop Now
                                                    </Link>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <h1
                                                    className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight drop-shadow-lg"
                                                    {...(i === 0 ? { "data-testid": "hero-heading" } : { "data-testid": `hero-banner-title-${s.id}` })}
                                                >
                                                    {s.title}
                                                </h1>
                                                {s.text && (
                                                    <p
                                                        className="mt-3 md:mt-5 text-base md:text-lg text-blue-50 leading-relaxed drop-shadow"
                                                        {...(i === 0 ? { "data-testid": "hero-subtitle" } : {})}
                                                    >
                                                        {s.text}
                                                    </p>
                                                )}
                                                <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
                                                    {s.kind === "hero" ? (
                                                        <>
                                                            <Link
                                                                to="/products"
                                                                data-testid="hero-explore-button"
                                                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#40E0D0] px-8 py-3.5 font-heading font-bold text-[#0033A0] transition-colors hover:bg-[#2fd4c4]"
                                                            >
                                                                <ShoppingBasket className="h-5 w-5" />
                                                                Explore Our Products
                                                            </Link>
                                                            <a
                                                                href={STORE.directionsUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-testid="hero-directions-button"
                                                                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white bg-white/10 px-8 py-3.5 font-heading font-bold text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-[#0033A0]"
                                                            >
                                                                <MapPin className="h-5 w-5" />
                                                                Find Our Store
                                                            </a>
                                                        </>
                                                    ) : (
                                                        s.cta_label &&
                                                        s.cta_url && (
                                                            <Link
                                                                to={s.cta_url}
                                                                data-testid={`hero-banner-cta-${s.id}`}
                                                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#40E0D0] px-8 py-3.5 font-heading font-bold text-[#0033A0] transition-all hover:bg-[#2fd4c4] hover:gap-3"
                                                            >
                                                                {s.cta_label}
                                                                <ArrowRight className="h-5 w-5" />
                                                            </Link>
                                                        )
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {count > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => go(index - 1)}
                                aria-label="Previous slide"
                                data-testid="hero-prev"
                                className="hidden sm:block absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 rounded-full bg-white/15 backdrop-blur-md border border-white/25 p-2.5 text-white hover:bg-white hover:text-[#0033A0] transition-colors"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => go(index + 1)}
                                aria-label="Next slide"
                                data-testid="hero-next"
                                className="hidden sm:block absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 rounded-full bg-white/15 backdrop-blur-md border border-white/25 p-2.5 text-white hover:bg-white hover:text-[#0033A0] transition-colors"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </>
                    )}
                </div>

                {count > 1 && (
                    <div className="flex justify-center gap-2 mt-4" data-testid="hero-dots">
                        {slides.map((s, i) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => go(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                data-testid={`hero-dot-${i}`}
                                className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-[#0033A0]" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
